import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Alert,
  Card
} from 'react-bootstrap';
import {
  Search,
  Plus,
  ExternalLink,
  Edit,
  Trash2,
  Star,
  X as XIcon
} from 'lucide-react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Hilfsfunktion zur Erzeugung eines zufälligen Pastell-Gradients
const generateRandomPastelGradient = () => {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  const factor = 0.7;
  const pr = Math.round(r + factor * (255 - r));
  const pg = Math.round(g + factor * (255 - g));
  const pb = Math.round(b + factor * (255 - b));
  return `linear-gradient(
    45deg,
    rgba(${pr}, ${pg}, ${pb}, 0.35) 0%,
    rgba(${pr}, ${pg}, ${pb}, 0.55) 100%
  )`;
};

// Statische CSS-Styles außerhalb der Komponente
const customStyles = `
  body {
    background-color: rgb(243, 242, 241) !important;
    padding-bottom: 2rem;
  }
  .hover-shadow {
    transition: all 0.3s ease !important;
  }
  .hover-shadow:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
  }
  .mobile-header {
    background-color: #F3F2F1;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .mobile-header h2 {
    font-weight: bold;
    margin: 0;
    font-size: 2rem;
    color: #333;
  }
  .mobile-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .mobile-search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }
  .mobile-search-container {
    position: absolute;
    right: 100%;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border-radius: 20px;
    margin-right: 8px;
  }
  .mobile-search-container.expanded {
    width: 200px;
    opacity: 1;
    transform: translateX(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  .mobile-search-container.collapsed {
    width: 0;
    opacity: 0;
    transform: translateX(20px);
    pointer-events: none;
  }
  .mobile-search-input {
    border: none;
    padding: 0.5rem 1rem;
    width: 100%;
    background: transparent;
    font-size: 0.95rem;
    color: #333;
  }
  .mobile-search-input:focus {
    outline: none;
  }
  .btn-icon {
    background: none;
    border: none;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 20px;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    position: relative;
    z-index: 2;
  }
  .btn-icon:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #0078d4;
  }
  .btn-icon.active {
    background-color: #f0f0f0;
    color: #0078d4;
  }
  .btn-new-link {
    background-color: #333;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  .btn-new-link:hover {
    background-color: #222;
  }
  @media (min-width: 768px) {
    .custom-search-input .input-group-text {
      border-radius: 6px 0 0 6px;
      background-color: #e9ecef;
      border: 1px solid #ced4da;
    }
    .custom-search-input .form-control {
      border-radius: 0 6px 6px 0;
      padding: 0.5rem 1rem;
      border: 1px solid #ced4da;
    }
    .btn-new-link {
      padding: 0.5rem 1rem;
      white-space: nowrap;
      min-width: auto;
    }
  }
  @media (max-width: 767px) {
    .row-cols-2 > * {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
`;

// Optimierte LinkCard-Komponente mit Memoisierung
const LinkCard = React.memo(({ link, onEdit, onDelete }) => {
  const headerBackground = useMemo(() => generateRandomPastelGradient(), []);

  return (
    <Card
      className="h-100 shadow-sm hover-shadow"
      style={{
        transition: 'all 0.3s ease',
        backgroundColor: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
      <Card.Body className="d-flex flex-column p-0">
        <div
          className="p-3"
          style={{
            background: headerBackground,
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div
              className="me-3 d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: '#ffffff',
                padding: '10px',
                borderRadius: '10px',
                width: '56px',
                height: '56px',
                flexShrink: 0,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              {link.icon ? (
                <img
                  src={link.icon}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }}
                />
              ) : (
                <Star size={24} style={{ color: '#0369a1' }} strokeWidth={1.5} />
              )}
            </div>
            <div className="d-flex gap-1">
              <Button
                variant="link"
                size="sm"
                onClick={() => onEdit(link)}
                className="p-1 d-flex align-items-center justify-content-center"
                style={{
                  color: '#64748b',
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                }}
                title="Bearbeiten"
              >
                <Edit size={14} />
              </Button>
              <Button
                variant="link"
                size="sm"
                onClick={() => onDelete(link.id)}
                className="p-1 d-flex align-items-center justify-content-center"
                style={{
                  color: '#64748b',
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                }}
                title="Löschen"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h3
              style={{
                fontWeight: '600',
                color: '#334155',
                fontSize: '0.95rem',
                margin: 0,
                lineHeight: '1.3',
                wordBreak: 'break-word',
                width: '100%'
              }}
            >
              {link.name}
            </h3>
          </div>
        </div>
        <div className="p-3" style={{ backgroundColor: '#ffffff' }}>
          <div className="mb-3">
            <p
              style={{
                fontSize: '0.85rem',
                color: '#475569',
                margin: 0,
                lineHeight: '1.4',
                wordBreak: 'break-word'
              }}
            >
              {link.description}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="light"
              size="sm"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-flex align-items-center justify-content-center gap-1 flex-grow-1"
              style={{
                fontSize: '0.85rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #e0f2fe',
                color: '#0369a1',
                padding: '8px',
                borderRadius: '8px'
              }}
            >
              <ExternalLink size={14} />
              Zum Tool
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
});

const LinkCollection = () => {
  const [links, setLinks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [editingLink, setEditingLink] = useState(null);
  const [newLink, setNewLink] = useState({
    name: '',
    description: '',
    url: '',
    icon: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const linksCollectionRef = collection(db, 'links');
        const linksSnapshot = await getDocs(linksCollectionRef);
        const linksList = linksSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data()
        }));
        setLinks(linksList);
      } catch (err) {
        console.error('Fehler beim Laden der Links:', err);
        setError('Links konnten nicht geladen werden');
        toast.error('Fehler beim Laden der Links');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLinks();
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingLink(null);
    setNewLink({ name: '', description: '', url: '', icon: '' });
  }, []);

  const handleEdit = useCallback((link) => {
    setEditingLink(link);
    setNewLink({ ...link });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Möchten Sie diesen Link wirklich löschen?')) {
      try {
        const linkDoc = doc(db, 'links', id);
        await deleteDoc(linkDoc);
        setLinks((prev) => prev.filter((l) => l.id !== id));
        toast.success('Link erfolgreich gelöscht');
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        toast.error('Fehler beim Löschen des Links');
      }
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        if (editingLink) {
          const linkDocRef = doc(db, 'links', editingLink.id);
          await updateDoc(linkDocRef, newLink);
          setLinks((prevLinks) =>
            prevLinks.map((l) =>
              l.id === editingLink.id ? { ...newLink, id: editingLink.id } : l
            )
          );
          toast.success('Link erfolgreich aktualisiert');
        } else {
          const linksCollectionRef = collection(db, 'links');
          const docRef = await addDoc(linksCollectionRef, newLink);
          setLinks((prevLinks) => [...prevLinks, { ...newLink, id: docRef.id }]);
          toast.success('Link erfolgreich erstellt');
        }
        handleCloseModal();
      } catch (err) {
        console.error('Fehler beim Speichern:', err);
        toast.error('Fehler beim Speichern des Links');
      }
    },
    [editingLink, newLink, handleCloseModal]
  );

  const filteredLinks = useMemo(
    () =>
      links.filter(
        (link) =>
          link.name.toLowerCase().includes(search.toLowerCase()) ||
          link.description.toLowerCase().includes(search.toLowerCase())
      ),
    [links, search]
  );

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Lädt...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Fehler</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Neu laden
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="p-0">
      <style type="text/css">{customStyles}</style>

      {/* Mobile Header */}
      <div className="mobile-header d-md-none">
        <div className="d-flex justify-content-between align-items-center">
          <h2>Toolbox</h2>
          <div className="mobile-actions">
            <div className="mobile-search-wrapper">
              <div className={`mobile-search-container ${showSearch ? 'expanded' : 'collapsed'}`}>
                <input
                  type="text"
                  className="mobile-search-input"
                  placeholder="Suche..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button
                className={`btn-icon ${showSearch ? 'active' : ''}`}
                onClick={() => setShowSearch((prev) => !prev)}
                aria-label="Suche"
              >
                {showSearch ? <XIcon size={20} /> : <Search size={20} />}
              </button>
            </div>
            <Button className="btn-new-link" onClick={() => setShowModal(true)}>
              <Plus size={16} />
              Neu
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="d-none d-md-block py-4">
        <h2
          className="text-center mb-4"
          style={{
            color: '#333',
            fontWeight: '600',
            fontSize: '2.4rem',
            letterSpacing: '0.5px'
          }}
        >
          Toolbox
        </h2>
        <div className="d-flex flex-column flex-md-row gap-2 mb-4 align-items-stretch px-3">
          <InputGroup className="flex-grow-1 custom-search-input">
            <InputGroup.Text>
              <Search size={16} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Suche nach Namen oder Beschreibung..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Button className="btn-new-link" onClick={() => setShowModal(true)}>
            <Plus size={16} />
            Neuer Link
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 mt-4 mt-md-0">
        <Row xs={2} md={3} lg={4} className="g-3">
          {filteredLinks.map((link) => (
            <Col key={link.id}>
              <LinkCard link={link} onEdit={handleEdit} onDelete={handleDelete} />
            </Col>
          ))}
        </Row>
      </div>

      {/* Modal für Erstellen/Bearbeiten */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingLink ? 'Link bearbeiten' : 'Neuer Link'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={newLink.name}
                onChange={(e) =>
                  setNewLink((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                type="text"
                value={newLink.description}
                onChange={(e) =>
                  setNewLink((prev) => ({ ...prev, description: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>URL</Form.Label>
              <Form.Control
                type="url"
                value={newLink.url}
                onChange={(e) =>
                  setNewLink((prev) => ({ ...prev, url: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Icon URL (optional)</Form.Label>
              <Form.Control
                type="url"
                value={newLink.icon}
                onChange={(e) =>
                  setNewLink((prev) => ({ ...prev, icon: e.target.value }))
                }
              />
              <Form.Text className="text-muted">
                Optional: URL zu einem Bild, das im Header angezeigt wird.
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                Abbrechen
              </Button>
              <Button variant="primary" type="submit">
                {editingLink ? 'Speichern' : 'Hinzufügen'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <ToastContainer position="bottom-right" />
    </Container>
  );
};

export default LinkCollection;